import React, { useState, useEffect } from 'react';
import { sendCollabRequest, acceptCollabRequest, CollabEntry } from '@/api/collaborations';
import { dismissCollabNotificationsFromUser, refreshNotifications } from '@/api/notifications';
import JWButton from '../../brand/JWButton';
import { ButtonProps } from '@/types/button';
import CollabInfoModal from './CollabInfoModal';
import { ProfileInfoType } from '@/types/profile';
import CollabEmailConfirmDialog from '../../layout/NotificationHub/CollabEmailConfirmDialog';
import { FiEye } from 'react-icons/fi';

interface CollabButtonProps extends Omit<ButtonProps, 'onClick'> {
    toUserId: string
    initialCollabStatus: 'none' | 'pending' | 'accepted'
    initiatedByOtherUser?: boolean
    icon?: React.ReactNode
    onRequestSent?: () => void
    collaboratorInfo?: ProfileInfoType
    pendingCollabId?: string
    collabHistory?: CollabEntry[]
    onCollabEnded?: (collabId: string) => void
    /** Runs before a new request starts. Return true to stop it, e.g. to ask a visitor to sign up first. */
    interceptRequest?: () => boolean
}

const CollabButton: React.FC<CollabButtonProps> = ({
    toUserId,
    initialCollabStatus,
    initiatedByOtherUser = false,
    icon,
    className,
    onRequestSent,
    collaboratorInfo,
    pendingCollabId,
    collabHistory,
    onCollabEnded,
    interceptRequest,
    shape: _shape,
    variant: _variant,
    ...props
}) => {
    const [isRequesting, setIsRequesting] = useState(false);
    const [collabStatus, setCollabStatus] = useState(initialCollabStatus);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [showEmailConfirmDialog, setShowEmailConfirmDialog] = useState(false);

    useEffect(() => {
        setCollabStatus(initialCollabStatus);
    }, [initialCollabStatus]);

    const handleCollabRequest = () => {
        if (interceptRequest?.()) return;
        if (!toUserId) return;
        setShowEmailConfirmDialog(true);
    };

    const handleConfirmSend = async () => {
        try {
            setIsRequesting(true);
            await sendCollabRequest(toUserId);
            setCollabStatus('pending');
            setShowEmailConfirmDialog(false);

            // Refresh notifications to show any new ones
            refreshNotifications();

            if (onRequestSent) onRequestSent();
        } catch (error) {
            console.error('Error sending collab request:', error);
        } finally {
            setIsRequesting(false);
        }
    };

    const handleAcceptCollabRequest = () => {
        setShowEmailConfirmDialog(true);
    };

    const handleConfirmAccept = async () => {
        try {
            setIsRequesting(true);

            // Accept the collaboration request
            const result = await acceptCollabRequest(pendingCollabId);

            // Also dismiss any pending collaboration notifications from this user
            if (toUserId) {
                await dismissCollabNotificationsFromUser(toUserId);
            }

            setCollabStatus('accepted');
            setShowEmailConfirmDialog(false);

            // Refresh notifications to show the collaboration notification
            // Make sure this is called after the request completes successfully
            refreshNotifications();
        } catch (error) {
            console.error('Error accepting collab request:', error);
        } finally {
            setIsRequesting(false);
        }
    };

    const handleCancelEmailConfirm = () => {
        setShowEmailConfirmDialog(false);
    };

    const handleSeeCollabInfo = () => {
        setIsModalOpen(true);
    };

    const getButtonContent = () => {
        switch (collabStatus) {
        case 'none':
            return { text: 'Collab', onClick: handleCollabRequest, disabled: isRequesting };
        case 'pending':
            if (initiatedByOtherUser && pendingCollabId) {
                return { text: 'Accept', onClick: handleAcceptCollabRequest, disabled: isRequesting };
            }
            return { text: 'Requested', onClick: undefined, disabled: true };
        case 'accepted':
            return { text: 'Collab Info', onClick: handleSeeCollabInfo, disabled: false };
        default:
            return { text: 'Collab', onClick: handleCollabRequest, disabled: isRequesting };
        }
    };

    const { text, onClick, disabled } = getButtonContent();
    const collabIcon = icon ? (
        <span className="inline-flex h-4 w-4 flex-shrink-0 items-center justify-center [&>svg]:h-4 [&>svg]:w-4">
            {icon}
        </span>
    ) : null;

    const showSplitCollabAndInfo =
        collabStatus === 'none' && (collabHistory?.length ?? 0) > 0;

    const collabMainButton = (
        <JWButton
            onClick={onClick}
            disabled={disabled}
            shape="default"
            variant="default"
            className={`flex items-center justify-center gap-2 transition-transform hover:scale-105 whitespace-nowrap ${
                text === 'Accept' ? '!bg-green-500 hover:!bg-green-600 !border-green-500 hover:!border-green-600' :
                    text === 'Collab Info' ? '!bg-blue-500 hover:!bg-blue-600 !border-blue-500 hover:!border-blue-600' : ''
            } ${className || ''}`}
            {...props}
        >
            {text === 'Collab Info' ? <FiEye className="h-5 w-5" /> : collabIcon}
            <span>{text}</span>
        </JWButton>
    );

    const splitCollabHistoryButton = (
        <div
            className={`flex w-full min-w-0 overflow-hidden rounded-md border border-white/20 shadow-sm ${className || ''}`}
        >
            <JWButton
                onClick={handleCollabRequest}
                disabled={isRequesting}
                shape="default"
                variant="default"
                className="!rounded-none flex min-h-10 min-w-0 flex-1 items-center justify-center gap-2 whitespace-nowrap !border-r !border-white/25 transition-transform hover:scale-105"
                {...props}
            >
                {collabIcon}
                <span>Collab</span>
            </JWButton>
            <button
                type="button"
                className="flex min-h-10 min-w-0 flex-1 items-center justify-center gap-2 bg-aquarius px-2 font-nunito text-lg text-white transition-transform duration-200 hover:scale-105"
                onClick={handleSeeCollabInfo}
            >
                <FiEye className="h-5 w-5 flex-shrink-0" />
                <span>Info</span>
            </button>
        </div>
    );

    return (
        <>
            {showSplitCollabAndInfo ? splitCollabHistoryButton : collabMainButton}
            <CollabInfoModal
                isOpen={isModalOpen}
                onClose={() => setIsModalOpen(false)}
                collaborator={collaboratorInfo}
                collabHistory={collabHistory}
                onEndCollab={onCollabEnded}
            />
            <CollabEmailConfirmDialog
                isOpen={showEmailConfirmDialog}
                onClose={handleCancelEmailConfirm}
                onAccept={collabStatus === 'none' ? handleConfirmSend : handleConfirmAccept}
                dialogType={collabStatus === 'none' ? 'send' : 'accept'}
            />

        </>
    );
};

export default CollabButton;
